import React, { useState, useEffect } from 'react';
import { Box, Button, Divider, Grid, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  createAgencyOperation,
  CreateOperationData,
  fetchOperationRoles,
  OperationLocation,
  OperationRole
} from '../../../networkRequest/operationsService';
import { useUser } from '../../../hooks/useUser';
import { useAgency } from '../../../hooks/useAgency';
import { PageNavigation, SearchableAgencyOfficialDropdownOption, Toast } from '@components';
import { combineDateAndTime } from '../../../utils/dateUtils';
import { validateOperationForm, isOperationFormValid } from '../../../utils/operationValidation';
import PageTitle from '../components/PageTitle';
import { EditOperation } from '../components';
import OperationAgencyOfficials from './components/OperationAgencyOfficials';
import OperationLocations from './components/OperationLocations';
import { COPY, OPERATION_AGENCY_OFFICIAL_ROLES, PAGE_PATHS } from '../../../constants'

export interface OperationAgencyOfficialAttributes {
  official: SearchableAgencyOfficialDropdownOption | null;
  assignment: string | null;
}

const defaultLocation: OperationLocation = {
  id: '',
  name: '',
  street_address_1: '',
  street_address_2: '',
  city: '',
  state: '',
  zip: '',
  primary: false
};

const CreateOperationPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { agency } = useAgency();
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const [formData, setFormData] = useState({
    title: '',
    agencyAssignedIdentifier: '',
    description: '',
    date: '',
    time: '',
    operationType: {
      planned: false,
      emergency: false
    }
  });

  const [primaryLocation, setPrimaryLocation] = useState<OperationLocation>({
    ...defaultLocation,
    primary: true
  });

  const [additionalLocations, setAdditionalLocations] = useState<OperationLocation[]>([]);

  const [incidentCommander, setIncidentCommander] = useState<OperationAgencyOfficialAttributes | null>(null);
  const [deputyIncidentCommanders, setDeputyIncidentCommanders] = useState<OperationAgencyOfficialAttributes[]>([]);
  const [commandStaff, setCommandstaff] = useState<OperationAgencyOfficialAttributes[]>([]);
  const [operationRoles, setOperationRoles] = useState<OperationRole[]>([]);

  const handleCancel = () => {
    // Navigate back to operations page
    navigate(PAGE_PATHS.operations);
  };

  const validateForm = () => {
    return validateOperationForm(formData, primaryLocation, { includeLocations: true });
  };

  const isFormValid = () => {
    return isOperationFormValid(formData, primaryLocation, { includeLocations: true });
  };

  const handleSave = async () => {
    if (!user?.accessToken || !agency?.id) {
      setToast({ message: 'Authentication required', type: 'error' });
      return;
    }

    // Validate form before making API call
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setToast({
        message: `Please fix the following errors: ${validationErrors.join(', ')}`,
        type: 'error'
      });
      return;
    }
    // comment
    setLoading(true);

    try {
      const operation_agency_officials_attributes = [
        ...(incidentCommander?.official ? [{
          agency_official_id: Number(incidentCommander.official.id),
          assignment: OPERATION_AGENCY_OFFICIAL_ROLES.INCIDENT_COMMANDER,
          operation_role_id: getRoleIdForAssignment(OPERATION_AGENCY_OFFICIAL_ROLES.INCIDENT_COMMANDER),
        }] : []),
        ...deputyIncidentCommanders.map(o => ({
          agency_official_id: Number(o.official!.id),
          assignment: OPERATION_AGENCY_OFFICIAL_ROLES.DEPUTY_INCIDENT_COMMANDER,
          operation_role_id: getRoleIdForAssignment(OPERATION_AGENCY_OFFICIAL_ROLES.DEPUTY_INCIDENT_COMMANDER)
        })),
        ...commandStaff.map(o => ({
          agency_official_id: Number(o.official!.id),
          assignment: o.assignment ?? OPERATION_AGENCY_OFFICIAL_ROLES.COMMAND_STAFF,
          operation_role_id: getRoleIdForAssignment(OPERATION_AGENCY_OFFICIAL_ROLES.COMMAND_STAFF)
        }))
      ];

      // Transform form data to match API interface
      const operationData: CreateOperationData = {
        title: formData.title,
        agency_assigned_identifier: formData.agencyAssignedIdentifier,
        description: formData.description,
        is_planned: formData.operationType.planned,
        is_emergency: formData.operationType.emergency,
        start_date: combineDateAndTime(formData.date, formData.time),
        status: 'planning',
        operation_locations_attributes: [
          {
            name: primaryLocation.name,
            street_address_1: primaryLocation.street_address_1,
            street_address_2: primaryLocation.street_address_2,
            city: primaryLocation.city,
            state: primaryLocation.state,
            zip: primaryLocation.zip,
            primary: true
          },
          ...additionalLocations.map((location) => ({
            name: location.name,
            street_address_1: location.street_address_1,
            street_address_2: location.street_address_2,
            city: location.city,
            state: location.state,
            zip: location.zip,
            primary: false
          }))
        ],
        operation_agency_officials_attributes // <-- include this
      };

      const { error } = await createAgencyOperation(user.accessToken, agency.id, operationData);

      if (error) {
        setToast({ message: error, type: 'error' });
      } else {
        setToast({ message: 'Operation created successfully', type: 'success' });
        // Navigate back to operations list after successful creation
        setTimeout(() => navigate(PAGE_PATHS.operations), 1500);
      }
    } catch (err) {
      setToast({ message: 'Failed to create operation', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handlePrimaryLocationChange = (field: string, value: string) => {
    setPrimaryLocation((prev) => ({ ...prev, [field]: value }));
  };

  const handleAdditionalLocationChange = (id: string, field: string, value: string) => {
    setAdditionalLocations((prev) =>
      prev.map((loc) => (loc.id === id ? { ...loc, [field]: value } : loc))
    );
  };

  const handleAddAdditionalLocation = () => {
    const newLocation = {
      id: Date.now().toString(),
      name: '',
      street_address_1: '',
      street_address_2: '',
      city: '',
      state: '',
      zip: '',
      primary: false
    };
    setAdditionalLocations((prev) => [...prev, newLocation]);
  };

  const handleDeleteLocation = (id: string) => {
    setAdditionalLocations((prev) => prev.filter((l) => l.id !== id));
  };

  const handleCommandStaffChange = (id: number, official: OperationAgencyOfficialAttributes | null) => {
    setCommandstaff((prev) =>
      prev.map((agent) => {
        return agent?.official?.id === id
          ? { ...agent, ...official }
          : agent
      })
    );
  };

  const handleAddCommandStaff = () => {
    setCommandstaff((prev) => [...prev, {
      official: {
        id: Date.now(),
        first_name: '',
        last_name: '',
      },
      assignment: null,
    }]);
  };

  const handleDeleteCommandStaff = (id: number) => {
    setCommandstaff((prev) => prev.filter((l) => l?.official?.id !== id))
  };

  const handleClearCommandStaff = (id: number) => {
    setCommandstaff((prev) =>
      prev.map((agent) => {
        return agent?.official?.id === id
          ? {
            official: {
              id: Date.now(),
              first_name: '',
              last_name: '',
            },
            assignment: null,
          }
          : agent
      })
    );
  };

  const handleDeputyIncidentCommanderChange = (id: number, official: OperationAgencyOfficialAttributes | null) => {
    setDeputyIncidentCommanders((prev) =>
      prev.map((agent) => {
        return agent?.official?.id === id
          ? { ...agent, ...official }
          : agent
      })
    );
  };

  const handleAddDeputyIncidentCommander = () => {
    setDeputyIncidentCommanders((prev) => [...prev, {
      official: {
        id: Date.now(),
        first_name: '',
        last_name: '',
      },
      assignment: null,
    }]);
  };

  const handleDeleteDeputyIncidentCommander = (id: number) => {
    setDeputyIncidentCommanders((prev) => prev.filter((l) => l?.official?.id !== id))
  };

  const handleClearDeputyIncidentCommander = (id: number) => {
    setDeputyIncidentCommanders((prev) =>
      prev.map((agent) => {
        return agent?.official?.id === id
          ? {
            official: {
              id: Date.now(),
              first_name: '',
              last_name: '',
            },
            assignment: null,
          }
          : agent
      })
    );
  };

  const getOperationRoles = async (user) => {
    const { data } = (await fetchOperationRoles(user.accessToken)) || {};
    if (data) {
      setOperationRoles(data);
    }
  };

  // Fetch operation roles on mount
  useEffect(() => {
    if (!user?.accessToken) return;
    getOperationRoles(user);
  }, [user?.accessToken]);

  const getRoleIdForAssignment = (assignment: string): string => {
    const match = operationRoles.find(role =>
      role.name.trim().toLowerCase() === assignment.trim().toLowerCase()
    );
    return match ? match.id : '';
  };

  return (
    <>
      <Stack spacing={2} sx={{ paddingBottom: '32px' }}>
        <PageNavigation
          breadcrumbs={[{ display: COPY.titles.operations, path: PAGE_PATHS.operations }]}
          controls={[
            <Button variant="outlined" color="primary" size="medium" onClick={handleCancel}>Cancel</Button>,
            <Button variant="contained" color="primary" size="medium" loading={loading} disabled={loading || !isFormValid()} onClick={handleSave}>
              Save Operation
            </Button>
          ]}
        />

        <Box sx={{  pb: 4.5 }}>
          <PageTitle title="New Operation" />
        </Box>

        <Stack spacing={4}>
          <EditOperation title="Details" formData={formData} onFormDataChange={setFormData} />

          <Divider sx={{ width: '100%' }} />

          <OperationLocations
            primaryLocation={primaryLocation}
            additionalLocations={additionalLocations}
            onPrimaryLocationChange={handlePrimaryLocationChange}
            onAdditionalLocationChange={handleAdditionalLocationChange}
            onAddLocation={handleAddAdditionalLocation}
            onDeleteLocation={handleDeleteLocation}
          />

          <Divider sx={{ width: '100%' }} />

          <OperationAgencyOfficials
            incidentCommander={incidentCommander}
            deputyIncidentCommanders={deputyIncidentCommanders}
            commandStaff={commandStaff}
            onDeleteCommandStaff={handleDeleteCommandStaff}
            onIncidentCommanderChange={(official: OperationAgencyOfficialAttributes | null) => setIncidentCommander(official)}
            onDeputyIncidentCommandersChange={handleDeputyIncidentCommanderChange}
            onAddDeputyIncidentCommander={handleAddDeputyIncidentCommander}
            onDeleteDeputyIncidentCommander={handleDeleteDeputyIncidentCommander}
            onClearDeputyIncidentCommander={handleClearDeputyIncidentCommander}
            onChangeCommandStaff={handleCommandStaffChange}
            onAddCommandStaff={handleAddCommandStaff}
            onClearCommandStaff={handleClearCommandStaff}
          />
        </Stack>
        <Divider sx={{ width: '100%' }} />
        <Grid
          container
          sx={{
            justifyContent: 'flex-end',
            gap: 2,
            paddingTop: '12px'
          }}>
          <Button variant="outlined" color="primary" size="medium" onClick={handleCancel}>Cancel</Button>
          <Button variant="contained" color="primary" size="medium" loading={loading} disabled={loading || !isFormValid()} onClick={handleSave}>
            Save Operation
          </Button>
        </Grid>
      </Stack>

      {toast && (
        <Toast type={toast.type} onClose={() => setToast(null)}>
          {toast.message}
        </Toast>
      )}
    </>
  );
};

export default CreateOperationPage;
